import { Link, LinkProps, useNavigate, useParams } from "react-router";
import { cn } from "~/lib/utils";

interface LocalizedLinkProps extends LinkProps {
  query?: Record<string, string | number | undefined>;
}

const useLocale = () => {
  const { lang } = useParams();
  const navigate = useNavigate();

  const LocalizedLink = ({ to, query, ...rest }: LocalizedLinkProps) => {
    let newTo: LinkProps["to"] = to;

    const buildQueryString = (queryObj?: Record<string, string | number | undefined>) => {
      if (!queryObj) return "";
      const params = new URLSearchParams();
      for (const key in queryObj) {
        const value = queryObj[key];
        if (value !== undefined) {
          params.set(key, String(value));
        }
      }
      return params.toString();
    };

    const queryString = buildQueryString(query);

    if (typeof to === "string") {
      const normalizedTo = to.startsWith("/") ? to : `/${to}`;
      const withQuery = queryString ? `${normalizedTo}?${queryString}` : normalizedTo;

      if (lang) {
        newTo = `/${lang}${withQuery}`;
      }
    } else if (typeof to === "object" && typeof to?.pathname === "string") {
      const normalizedPath = to?.pathname?.startsWith("/") ? to?.pathname : `/${to?.pathname}`;
      const fullPath = queryString ? `${normalizedPath}?${queryString}` : normalizedPath;

      if (lang) {
        newTo = {
          ...to,
          pathname: `/${lang}${fullPath}`,
        };
      }
    }

    return <Link to={newTo} {...rest} className={cn("!no-underline", rest.className)} />;
  };

  const localizedNavigate = (to: string, options?: { replace?: boolean; state?: unknown }) => {
    const normalizedTo = to.startsWith("/") ? to : `/${to}`;
    const finalTo = `/${lang}${normalizedTo}`;
    navigate(finalTo, options);
  };

  return { LocalizedLink, navigate: localizedNavigate };
};

export default useLocale;
